import { getWorkspaceIdentitySession, signInWithWorkspaceCustomToken } from '../../features/auth/centralIdentityAuth';
import { storeLocalDevSession } from '../../features/auth/localDevSession';
import { usesCentralIdentity } from '../../features/auth/sessionAccess';
import { getLandingUrl } from '../../shared/config/landingUrl';
import { getTenantRuntime } from './tenantRuntime';

export const WORKSPACE_ENTRY_PARAM = 'entry';
const RECOVERY_KEY = 'lineside.workspace-entry-recovery';
const PENDING_KEY = 'lineside.workspace-entry-pending';
const RECOVERY_WINDOW_MS = 10 * 60 * 1000;
// Credentials live only in memory while bootstrap is recovering.
let pendingEntry = '';
let pendingCustomToken = '';
let inFlight = null;

function clearEntryRecovery() {
  try {
    window.sessionStorage.removeItem(RECOVERY_KEY);
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch { /* optional storage */ }
}

async function entryFetch(url, options) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
}

export function workspaceEntryRecoveryUrl({ automatic = false } = {}) {
  try {
    const last = Number(window.sessionStorage.getItem(RECOVERY_KEY));
    if (automatic && last && Date.now() - last < RECOVERY_WINDOW_MS) return null;
    window.sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));
  } catch {
    // Without loop protection, recovery must be an explicit click.
    if (automatic) return null;
  }
  const url = new URL(getLandingUrl('/signup') || 'https://lineside.ai/signup');
  url.searchParams.set('workspace_entry', 'resume');
  url.searchParams.set('workspace_host', window.location.hostname);
  return url.toString();
}

async function existingWorkspaceSession(apiBaseUrl) {
  try {
    const session = await getWorkspaceIdentitySession();
    if (!session?.access_token) return null;
    // An existing Firebase session alone does not prove access to this tenant.
    const response = await entryFetch(`${apiBaseUrl}/api/organization/me`, {
      headers: { Authorization: `Bearer ${session.access_token}`, Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const access = await response.json();
    return access?.organization_id && access?.profile_id && access?.role_slug ? session : null;
  } catch {
    return null;
  }
}

export function readWorkspaceEntryFromLocation(location = window.location) {
  const params = new URLSearchParams(location.search);
  return String(params.get(WORKSPACE_ENTRY_PARAM) || '').trim();
}

export function stripWorkspaceEntryFromLocation(location = window.location) {
  const params = new URLSearchParams(location.search);
  if (!params.has(WORKSPACE_ENTRY_PARAM)) {
    return `${location.pathname}${location.search}${location.hash}`;
  }
  params.delete(WORKSPACE_ENTRY_PARAM);
  const search = params.toString();
  const next = `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
  window.history.replaceState({}, '', next);
  return next;
}

async function redeemPendingEntry() {
  if (!usesCentralIdentity()) return null;
  const entry = readWorkspaceEntryFromLocation();
  if (entry) {
    pendingEntry = entry;
    pendingCustomToken = '';
    stripWorkspaceEntryFromLocation();
    try { window.sessionStorage.setItem(PENDING_KEY, String(Date.now())); } catch { /* optional storage */ }
  }
  const apiBaseUrl = getTenantRuntime().apiBaseUrl;
  if (!pendingEntry && !pendingCustomToken) {
    let interrupted = false;
    try {
      const since = Number(window.sessionStorage.getItem(PENDING_KEY));
      interrupted = since > 0 && Date.now() - since < RECOVERY_WINDOW_MS;
    } catch { /* optional storage */ }
    if (!interrupted) return null;
    const session = await existingWorkspaceSession(apiBaseUrl);
    if (session) { clearEntryRecovery(); return session; }
    // A refresh lost in-memory credentials. Get a fresh ticket through signup.
    const error = new Error('Workspace sign-in was interrupted.');
    error.code = 'entry_expired';
    throw error;
  }
  try {
    if (!pendingCustomToken) {
      const response = await entryFetch(`${apiBaseUrl}/api/platform/onboarding-plan/workspace-entry/redeem`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entry: pendingEntry }),
      });
      if (!response.ok) {
        const error = new Error(response.status === 409
          ? 'This sign-in link has expired or was already used.'
          : 'We could not finish opening your workspace.');
        error.code = response.status === 409 ? 'entry_expired' : 'entry_unavailable';
        throw error;
      }
      const payload = await response.json();
      if (payload?.localSession && import.meta.env.DEV) {
        // A local backend opens the local workspace with a local session.
        const session = storeLocalDevSession(payload.localSession);
        pendingEntry = '';
        clearEntryRecovery();
        return session;
      }
      if (!payload?.customToken) {
        throw new Error('Workspace entry redeem did not return a sign-in token');
      }
      pendingCustomToken = payload.customToken;
    }
    const session = await signInWithWorkspaceCustomToken(pendingCustomToken);
    pendingEntry = '';
    pendingCustomToken = '';
    clearEntryRecovery();
    return session;
  } catch (error) {
    const session = await existingWorkspaceSession(apiBaseUrl);
    if (session) {
      pendingEntry = '';
      pendingCustomToken = '';
      clearEntryRecovery();
      return session;
    }
    if (['auth/invalid-custom-token', 'auth/custom-token-mismatch'].includes(error.code)) {
      pendingCustomToken = '';
      error.code = 'entry_expired';
    }
    throw error;
  }
}

export function consumeWorkspaceEntry() {
  // React StrictMode and repeated retry clicks share one redemption.
  if (!inFlight) inFlight = redeemPendingEntry().finally(() => { inFlight = null; });
  return inFlight;
}
